import React from 'react';
import type {StyleProp, TextStyle} from 'react-native';
import {Platform} from 'react-native';

import {openPageLink} from '../../utils/navigation';
import Text from '../themed/Text';

export type AlertSegment =
  | {type: 'text'; value: string}
  | {type: 'link'; value: string; href: string};

// Markdown `[label](href)` OR a bare http(s) URL. Ordered alternation so a
// markdown link is preferred over the bare-URL branch for the same span.
const LINK_RE = /\[([^\]]+)\]\(([^)\s]+)\)|(https?:\/\/[^\s)]+)/g;

/**
 * Split an alert message into ordered plain-text and link segments. Supports
 * markdown links (`[label](url)`) and bare URLs. Returns a single text segment
 * when there are no links.
 */
export const parseAlertMessage = (message: string): AlertSegment[] => {
  const segments: AlertSegment[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  LINK_RE.lastIndex = 0;

  while ((match = LINK_RE.exec(message)) !== null) {
    if (match.index > lastIndex) {
      segments.push({type: 'text', value: message.slice(lastIndex, match.index)});
    }
    if (match[1] && match[2]) {
      segments.push({type: 'link', value: match[1], href: match[2]});
    } else {
      segments.push({type: 'link', value: match[3], href: match[3]});
    }
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < message.length) {
    segments.push({type: 'text', value: message.slice(lastIndex)});
  }
  return segments;
};

type AlertMessageProps = {
  segments: AlertSegment[];
  textStyle?: StyleProp<TextStyle>;
  linkStyle?: StyleProp<TextStyle>;
};

/**
 * Renders an alert message, turning inline links into styled, tappable text.
 * Links route through `openPageLink`, which keeps own-domain URLs in-app and
 * sends external URLs to the browser.
 */
const AlertMessage: React.FC<AlertMessageProps> = ({segments, textStyle, linkStyle}) => (
  <Text variant="body2" style={textStyle}>
    {segments.map((seg, i) =>
      seg.type === 'link' ? (
        <Text
          key={i}
          variant="body2"
          testID="global-alert-link"
          style={linkStyle}
          onPress={() => openPageLink(seg.href)}
          accessibilityRole="link"
          {...(Platform.OS === 'web' ? {'aria-label': seg.value} : null)}>
          {seg.value}
        </Text>
      ) : (
        <React.Fragment key={i}>{seg.value}</React.Fragment>
      ),
    )}
  </Text>
);

export default AlertMessage;
